import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Sparkles, ArrowRight, Zap, Infinity as InfinityIcon, Crown, X } from "lucide-react";
import PayPalCheckoutModal from "./PayPalCheckoutModal";
import { useAuth } from "../lib/auth";

const PLANS = [
  {
    id: "free",
    name: "Taste",
    tagline: "See if we click.",
    priceMonthly: 0,
    priceYearly: 0,
    labelMonthly: "€0",
    labelYearly: "€0",
    cadenceMonthly: "forever",
    cadenceYearly: "forever",
    icon: Sparkles,
    features: [
      { t: "10 AI chat messages / day", on: true },
      { t: "3 voice pronunciation scores / day", on: true },
      { t: "Full free dictionary (all 74 words)", on: true },
      { t: "Daily phrase challenge + trophies", on: true },
      { t: "A1 & A2 lessons only", on: true },
      { t: "Spaced-repetition flashcards", on: false },
      { t: "Priority voice & faster responses", on: false },
    ],
    cta: "Start free",
    highlight: false,
  },
  {
    id: "plus",
    name: "Plus",
    tagline: "For regular learners.",
    priceMonthly: 17.99,
    priceYearly: 149,
    labelMonthly: "€17.99",
    labelYearly: "€149",
    cadenceMonthly: "/ month",
    cadenceYearly: "/ year",
    saveCopy: "Most learners pick this",
    // Effective €12.42 / month billed yearly — save €66 / year
    savingsYearly: Math.round(17.99 * 12 - 149),
    icon: Zap,
    features: [
      { t: "Unlimited AI chat (fair use)", on: true },
      { t: "60 voice pronunciation scores / day", on: true },
      { t: "All CEFR lessons A1 → C1", on: true },
      { t: "SM-2 spaced-repetition flashcards", on: true },
      { t: "Record + playback in flashcards", on: true },
      { t: "Priority TTS voices", on: true },
      { t: "Advanced grammar deep-dives", on: false },
    ],
    cta: "Go Plus",
    highlight: false,
  },
  {
    id: "pro",
    name: "Pro",
    tagline: "For serious fluency.",
    priceMonthly: 29.99,
    priceYearly: 299,
    labelMonthly: "€29.99",
    labelYearly: "€299",
    cadenceMonthly: "/ month",
    cadenceYearly: "/ year",
    saveCopy: "Most popular",
    savingsYearly: Math.round(29.99 * 12 - 299),
    icon: Crown,
    features: [
      { t: "Everything in Plus", on: true },
      { t: "Unlimited voice pronunciation scoring", on: true },
      { t: "Advanced grammar deep-dives + C2 content", on: true },
      { t: "Personalised weekly progress report", on: true },
      { t: "Email priority support (<24h)", on: true },
      { t: "Early access to every new feature", on: true },
      { t: "Cancel anytime — keep 30 days' history", on: true },
    ],
    cta: "Go Pro",
    highlight: true,
  },
  {
    id: "lifetime",
    name: "Lifetime",
    tagline: "Pay once. Speak forever.",
    priceMonthly: 499,
    priceYearly: 499,
    labelMonthly: "€499",
    labelYearly: "€499",
    strikeLabel: "€899",
    cadenceMonthly: "one-time",
    cadenceYearly: "one-time",
    saveCopy: "Launch offer · -44%",
    icon: InfinityIcon,
    features: [
      { t: "Everything in Pro", on: true },
      { t: "All future features included", on: true },
      { t: "Lifetime voice + chat (fair use)", on: true },
      { t: "Founder badge on profile", on: true },
      { t: "Direct line to the team", on: true },
    ],
    cta: "Buy lifetime",
    highlight: false,
    launchOffer: true,
  },
];

export default function PricingSection() {
  const [cycle, setCycle] = useState("yearly"); // monthly | yearly
  const [checkoutPlan, setCheckoutPlan] = useState(null); // plan_key string
  const { user, refreshUser } = useAuth() || {};
  const navigate = useNavigate();

  const openCheckout = (plan) => {
    if (plan.id === "free") { navigate("/register"); return; }
    if (!user) {
      // send them to register with a return marker so they come back to pricing
      navigate(`/register?next=${encodeURIComponent(`/#pricing:${plan.id}:${cycle}`)}`);
      return;
    }
    if (plan.id === "lifetime") {
      setCheckoutPlan("lifetime");
    } else {
      setCheckoutPlan(`${plan.id}_${cycle}`); // plus_monthly, pro_yearly, etc.
    }
  };

  const onCheckoutSuccess = async () => {
    setCheckoutPlan(null);
    try { await refreshUser?.(); } catch (_e) { /* noop */ }
    navigate("/app");
  };

  return (
    <section className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-24" data-testid="landing-pricing">
      <div className="text-center mb-12">
        <div className="text-xs tracking-[0.3em] uppercase text-peach-500 font-bold mb-3">Pricing</div>
        <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight">
          Pay for progress. <span className="gradient-text">Not for ads.</span>
        </h2>
        <p className="mt-4 text-[#A89BC9] max-w-xl mx-auto">
          An AI tutor is expensive to run well. Your subscription pays for the minutes we spend
          listening to you — not for marketing. Cancel anytime. Prices in euros.
        </p>

        {/* Monthly / Yearly toggle */}
        <div className="mt-8 inline-flex items-center p-1 rounded-full bg-white/5 border border-white/10 backdrop-blur" data-testid="pricing-cycle-toggle">
          <button
            onClick={() => setCycle("monthly")}
            data-testid="pricing-cycle-monthly"
            className={`px-5 py-2 rounded-full text-xs font-semibold tracking-wider uppercase transition ${
              cycle === "monthly" ? "bg-white text-navy-900" : "text-[#cdc1ed] hover:text-white"
            }`}
          >Monthly</button>
          <button
            onClick={() => setCycle("yearly")}
            data-testid="pricing-cycle-yearly"
            className={`relative px-5 py-2 rounded-full text-xs font-semibold tracking-wider uppercase transition ${
              cycle === "yearly" ? "bg-peach-500 text-navy-900" : "text-[#cdc1ed] hover:text-white"
            }`}
          >
            Yearly
            <span className="absolute -top-2 -right-2 bg-mint-400 text-navy-900 text-[9px] tracking-wider font-bold px-1.5 py-0.5 rounded-full border border-navy-900">Save</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {PLANS.map((plan) => {
          const isYearlyMode = cycle === "yearly";
          const priceLabel = isYearlyMode ? plan.labelYearly : plan.labelMonthly;
          const cadence = isYearlyMode ? plan.cadenceYearly : plan.cadenceMonthly;
          const Icon = plan.icon;
          const showSavings = isYearlyMode && plan.savingsYearly && plan.savingsYearly > 0;
          return (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.4 }}
              data-testid={`pricing-${plan.id}`}
              className={`relative rounded-3xl p-6 md:p-7 border transition-all flex flex-col ${
                plan.highlight
                  ? "bg-gradient-to-br from-peach-500/15 via-violet-500/10 to-mint-500/10 border-peach-500/40 peach-glow lg:-translate-y-2"
                  : plan.launchOffer
                  ? "bg-gradient-to-br from-mint-500/10 via-navy-800/70 to-navy-800/70 border-mint-400/30"
                  : "bg-navy-800/80 border-white/10 hover:border-white/25"
              }`}
            >
              {plan.saveCopy && (
                <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] tracking-[0.2em] uppercase font-bold whitespace-nowrap ${
                  plan.launchOffer
                    ? "bg-mint-400 text-navy-900"
                    : plan.highlight
                    ? "bg-peach-500 text-navy-900"
                    : "bg-white/10 text-white border border-white/20"
                }`}>
                  {plan.saveCopy}
                </div>
              )}

              <div className="flex items-center gap-2.5">
                <div className={`h-9 w-9 rounded-2xl flex items-center justify-center ${
                  plan.highlight
                    ? "bg-peach-500/20 text-peach-300 border border-peach-500/30"
                    : plan.launchOffer
                    ? "bg-mint-400/15 text-mint-300 border border-mint-400/30"
                    : "bg-white/5 text-[#cdc1ed] border border-white/10"
                }`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div>
                  <div className="font-heading font-bold text-lg leading-none">{plan.name}</div>
                  <div className="text-xs text-[#A89BC9] mt-0.5">{plan.tagline}</div>
                </div>
              </div>

              <div className="mt-5 flex items-baseline gap-2 flex-wrap">
                <div className="font-heading font-bold text-4xl md:text-5xl tracking-tight">
                  {priceLabel}
                </div>
                <div className="text-xs text-[#A89BC9]">{cadence}</div>
                {plan.strikeLabel && (
                  <div className="text-sm text-[#7d6fa7] line-through decoration-mint-400/60 ml-1">
                    {plan.strikeLabel}
                  </div>
                )}
              </div>
              {showSavings && (
                <div className="mt-1 text-[10px] text-mint-300">
                  Saves €{plan.savingsYearly} / year vs monthly
                </div>
              )}
              {plan.launchOffer && (
                <div className="mt-1 text-[10px] text-mint-300 font-semibold">
                  Regular price €899 · limited-time launch price
                </div>
              )}

              <ul className="mt-5 space-y-2.5 text-sm flex-1">
                {plan.features.map((f) => (
                  <li key={f.t} className="flex items-start gap-2">
                    {f.on ? (
                      <Check className={`h-4 w-4 mt-0.5 shrink-0 ${
                        plan.highlight ? "text-peach-300"
                          : plan.launchOffer ? "text-mint-300"
                          : "text-mint-400"
                      }`} />
                    ) : (
                      <X className="h-4 w-4 mt-0.5 shrink-0 text-[#4a3a6b]" />
                    )}
                    <span className={f.on ? "text-white/85" : "text-[#7d6fa7] line-through decoration-white/10"}>{f.t}</span>
                  </li>
                ))}
              </ul>

              <button
                type="button"
                onClick={() => openCheckout(plan)}
                data-testid={`pricing-${plan.id}-cta`}
                className={`mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold text-sm transition ${
                  plan.highlight
                    ? "bg-peach-500 text-navy-900 hover:bg-peach-400"
                    : plan.launchOffer
                    ? "bg-mint-400 text-navy-900 hover:bg-mint-300"
                    : plan.id === "free"
                    ? "bg-white/10 text-white hover:bg-white/15 border border-white/15"
                    : "border border-white/20 text-white hover:bg-white/5"
                }`}
              >
                {plan.cta} <ArrowRight className="h-4 w-4" />
              </button>
            </motion.div>
          );
        })}
      </div>

      <div className="mt-8 text-center text-[11px] tracking-wide text-[#7d6fa7]">
        All paid plans come with a <span className="text-[#cdc1ed]">7-day money-back guarantee</span>. No questions asked.
      </div>

      {checkoutPlan && (
        <PayPalCheckoutModal
          planKey={checkoutPlan}
          onClose={() => setCheckoutPlan(null)}
          onSuccess={onCheckoutSuccess}
        />
      )}
    </section>
  );
}
